<!-- Modern Ticket View Architecture - Aligned with add-edit-ticket -->
<div class="ticket-drawer" [formGroup]="sectionForm">
  @if (isLoading) {
    <app-panel-loading-skeleton entityLabel="ticket"
      [sidebarSections]="[{ title: 'Status & Priority', fields: ['Status', 'Priority'] }, { title: 'Ticket Details', fields: ['Ticket Type', 'Scopes', 'Customer', 'Contact', 'Vendor', 'Subcontractor', 'Responsible Party'] }, { title: 'Dates', fields: ['Due Date'] }]"
      infoTitle="Details" [infoFields]="['Status', 'Priority', 'Due Date', 'Customer', 'Assignee', 'Created On']"
      [tabs]="['Details', 'Checklist', 'Materials & Services', 'Attachments', 'Comments', 'History']"
      (closeRequested)="close()"></app-panel-loading-skeleton>
  }
  <div class="ticket-drawer-layout">

    <!-- LEFT: SIDEBAR (256px, collapsible) - VIEW MODE -->
    <app-ticket-sidebar [ticketForm]="sectionForm" [projectName]="getProjectName()" [ticketId]="ticketID"
      [ticketCode]="model?.code" [mode]="'view'" [milestones]="milestones" [selectedMilestone]="getSelectedMilestone()"
      [assignees]="model?.responsible_persons || []" [observers]="model?.observers || []"
      [participants]="model?.participants || []" [tags]="ticketTags" [completionPercentage]="completionPercentage"
      [checklist]="model?.check_list || []" [createdOn]="model?.created_at" [isCollapsed]="isSidebarCollapsed"
      [isReadOnly]="false" [isViewMode]="true" [statuses]="statuses" [selectedStatus]="model?.status_label"
      [ticketTypes]="ticketTypesList" [scopes]="scopes" [customers]="[]" [vendors]="[]" [subcontractors]="[]"
      [contacts]="[]" [selectedCustomer]="selectedCustomer" [selectedVendor]="selectedVendor"
      [selectedSubcontractor]="selectedSubcontractor" [selectedContact]="selectedContact"
      [selectedResponsibleParty]="selectedResponsibleParty" [showModuleControls]="false" [isLocked]="isLocked"
      (onCollapse)="isSidebarCollapsed = $event" (onSearch)="handleSearch($event)"
      (statusChange)="onChangeStatus($event)" (priorityChange)="onPriorityChange($event)"
      (tagSelectorOpened)="openTagSelector()" (tagsUpdated)="onTagsUpdated($event)">
    </app-ticket-sidebar>

    <!-- RIGHT: MAIN CONTENT AREA -->
    <div class="ticket-main-area">

      <!-- HEADER - VIEW MODE -->
      <app-ticket-header [isEdit]="false" [isViewMode]="true" [mode]="'view'" [ticketTitle]="model?.name"
        [moduleLabel]="model?.module?.name" [projectName]="model?.reference?.name" [projectId]="model?.reference?.id"
        [recordRoute]="getRecordRoute()"
        [status]="model?.status_label?.name" [statusColor]="model?.status_label?.color"
        [milestone]="getSelectedMilestone()" [isFullscreen]="isFullscreen" [isFormValid]="true"
        [searchQuery]="searchQuery" [isBillable]="isBillable" [isHighPriority]="model?.priority == 3"
        [priority]="model?.priority || 2" [statuses]="statuses" [selectedStatus]="model?.status_label"
        [hasStatuses]="statuses && statuses.length > 0" [showStatusDropdown]="true" [showPdfExport]="true"
        [showCopy]="true" [showDelete]="false" [showEdit]="true" [showMarkComplete]="true" [showNewTicket]="true"
        [pdfExportPermission]="exportPDFPermission" [markCompletePermission]="changeStatusPermission"
        [copyPermission]="'TICKET-COPY-TICKETS'"
        [statusChangePermission]="changeStatusPermission" [editPermission]="editPermission"
        [createPermission]="createPermission" [newTicketPermission]="createPermission" [isLocked]="isLocked"
        (close)="close(loadListingOnClose)" (recordLinkClick)="close(loadListingOnClose)"
        (delete)="onConfirmation()" (edit)="editTask()" (newTicket)="createTicket()"
        (markComplete)="markAsComplete()" (searchToggle)="isSearchOpen = !isSearchOpen"
        (searchChange)="searchQuery = $event" (fullscreenToggle)="isFullscreen = !isFullscreen"
        (statusSelect)="onChangeStatus($event)" (pdfExport)="generatePDF()" (copy)="openCopyTicketDialog()"
        (lockToggle)="toggleLock()">
      </app-ticket-header>

      <!-- MAIN CONTENT WITH TABS - VIEW MODE -->
      <app-ticket-main-content [ticketForm]="sectionForm" [mode]="'view'" [selectedMilestone]="getSelectedMilestone()"
        [checklistCount]="model?.check_list?.length || 0"
        [materialCount]="model?.ticket_items?.length || 0"
        [attachmentCount]="model?.relations?.attach_files?.length || 0" [commentCount]="ticketComments.length"
        [historyCount]="ticketHistory.length || 0" [activeTabIndex]="activeTabIndex" [isTicketModule]="false"
        [modulesList]="[]" [searchParams]="{}" [selectedItem]="{}" [showSearchInput]="false" [mentions]="[]"
        [isViewMode]="true" [showTimeElapsed]="true" [hideDetailsCommentsHistory]="false" [isLocked]="isLocked"
        [showMaterials]="showMaterialsTab"
        (tabKeyChange)="handleTabKeyChange($any($event))"
        (tabChange)="handleTabChange($event)" (editDescription)="editTask()">

        <!-- DETAILS TAB CONTENT -->
        <div details-tab>
          <app-ticket-details-tab [ticketForm]="sectionForm" [status]="model?.status_label?.name" [statuses]="statuses"
            [statusId]="model?.status" [priority]="getPriorityText(model?.priority || 2)" [dueDate]="model?.due_date"
            [startDate]="model?.start_date" [endDate]="model?.date_finished"
            [stage]="model?.stage?.name" [assignee]="model?.created_by?.full_name" [createdDate]="model?.created_at"
            [ticketType]="model?.ticket_type?.name" [customer]="model?.customer?.company_name"
            [vendor]="model?.vendor?.name" [subcontractor]="model?.subcontractor?.name" [scopes]="model?.scopes">
          </app-ticket-details-tab>
        </div>

        <!-- CHECKLIST TAB CONTENT -->
        <div checklist-tab>
          <div class="tab-content-wrapper">
            <app-ticket-checklists [model]="model" [ticketId]="ticketID" [checkLists]="model?.check_list"
              [isLocked]="isLocked" (callback)="handleChecklistChange($event)"
              [ticketItems]="model?.ticket_items" [showMaterials]="showMaterialsTab">
            </app-ticket-checklists>
          </div>
        </div>

        <!-- MATERIALS & SERVICES TAB CONTENT -->
        @if (showMaterialsTab) {
        <div materials-tab>
          <div class="tab-content-wrapper">
            <app-ticket-materials-services [materialServices]="model?.ticket_items"
              [jobId]="model?.job_id || model?.reference?.id" [ticketId]="ticketID"
              [isReadOnly]="isLocked" (callback)="handleMaterialServicesChange($event)">
            </app-ticket-materials-services>
          </div>
        </div>
        }

        <!-- ATTACHMENTS TAB CONTENT -->
        <div attachments-tab>
          <div class="tab-content-wrapper">
            <app-ticket-attachments [module_id]="TICKET_MODULE_ID" [attachable_id]="ticketID?.toString() || ''"
              [isLocked]="isLocked">
            </app-ticket-attachments>
          </div>
        </div>

        <!-- COMMENTS TAB CONTENT -->
        <div comments-tab>
          <div class="tab-content-wrapper">
            @if (model) {
              <app-ticket-comments [model]="model" [isActiveTab]="activeTabKey === 'comments'"
                [isLocked]="isLocked">
              </app-ticket-comments>
            }
          </div>
        </div>

        <!-- TIME ELAPSED TAB CONTENT -->
        <div time-elapsed-tab>
          <div class="tab-content-wrapper">
            @if (model) {
              <app-ticket-time-elapsed [model]="model">
              </app-ticket-time-elapsed>
            }
          </div>
        </div>

        <!-- HISTORY TAB CONTENT -->
        <div history-tab>
          @if (model) {
            <app-ticket-history-tab [historyData]="ticketHistory" [ticketId]="ticketID.toString()"
              [ticketModel]="model" (historyChange)="ticketHistory = $event">
            </app-ticket-history-tab>
          }
        </div>

      </app-ticket-main-content>
    </div>
  </div>

  <!-- FOOTER - VIEW MODE -->
  <div class="ticket-footer">
    <div class="footer-left">
      <!-- Favorite Toggle Button -->
      <button type="button" mat-button class="footer-action-btn" (click)="toggleFavorite()"
        [matTooltip]="isFavorite ? 'Remove from favorites' : 'Add to favorites'"
        [disabled]="isLocked" [ngClass]="{'permission-disabled': isLocked}">
        <mat-icon>{{ isFavorite ? 'star' : 'star_border' }}</mat-icon>
        <span class="btn-text">{{ isFavorite ? 'Favorited' : 'Add to Favorites' }}</span>
      </button>

      <span class="footer-separator">|</span>

      <!-- Set Reminder Button -->
      <button type="button" mat-button class="footer-action-btn" (click)="setReminder()"
        [attr.aria-label]="'Set reminder'" matTooltip="Set reminder"
        [disabled]="isLocked" [ngClass]="{'permission-disabled': isLocked}">
        <mat-icon>notifications_none</mat-icon>
        <span class="btn-text">Set Reminder</span>
      </button>

      <span class="footer-separator">|</span>

      <!-- Send Email Button -->
      <button type="button" mat-button class="footer-action-btn"
        (click)="composeEmail()"
        [attr.aria-label]="'Send email'"
        matTooltip="Send email">
        <mat-icon>email</mat-icon>
        <span class="btn-text">Send Email</span>
      </button>
    </div>

    <div class="footer-right">
      <!-- Delete Button -->
      <button type="button" mat-stroked-button class="footer-delete-btn" [appPermission]="deletePermission"
        (click)="onConfirmation()" [disabled]="isLocked" [ngClass]="{'permission-disabled': isLocked}">
        <mat-icon>delete</mat-icon>
        <span class="btn-text">Delete</span>
      </button>

      <!-- Close Button -->
      <button type="button" mat-raised-button class="footer-save-btn" (click)="close(loadListingOnClose)">
        <span class="btn-text">Close</span>
      </button>
    </div>
  </div>
</div>